import {
  GridRenderCellParams,
  GridColumns,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next'; // eslint-disable-line
import { baseCell } from '../cells/base-cell';
import { booleanCell } from '../cells/boolean-cell';
import {
  booleanFilterOperators,
  dateFilterOperators,
  filterOperators,
  numberFilterOperators
} from '../../../../constants';
import { currencyFormatter } from 'helpers/currency-formatter';
import { currencyCell } from '../cells/currency-cell';
import { dateCell } from '../cells/date-cell';
import { isDirector } from 'components/helpers/IsDirector';

export function OrderDetailColumns(): GridColumns {
  const { t } = useTranslation();
  const translationPrefix = 'entities.order_line';
  const isDir = isDirector();

  const statusOptions: Array<{ value: string; label: string }> = [
    { value: 'PENDING', label: t('entities.order_line.PENDING') },
    { value: 'COMPLETED', label: t('entities.order_line.COMPLETED') },
    { value: 'CANCELED', label: t('entities.order_line.CANCELED') }
  ];

  const statusColor = (statusString: string) => {
    switch (statusString) {
      case 'PENDING':
        return 'text-orange-500';
      case 'COMPLETED':
        return 'text-green-500';
      case 'CANCELED':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  // const strikedThroughRenderCell = (params: GridCellParams) =>
  //   params.row.status === 'CANCELED' && 'line-through';

  const translateStatus = (status: string) => i18n.t(`entities.order_line.${status}`);

  const columns: GridColumns = [
    // {
    //   field: 'id',
    //   headerName: t(`global.id`),
    //   width: 150,
    //   type: 'number',
    //   filterable: false,
    //   renderCell: baseCell
    // },
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 150,
      filterOperators,
      valueGetter: (params) => params.row.product.code,
      renderCell: baseCell
    },
    {
      field: 'product_description',
      headerName: t(`global.product_description`),
      width: 250,
      filterOperators,
      valueGetter: (params) => params.row.product.description,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      width: 250,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'is_periodic',
      headerName: t('entities.order_line.is_periodic'),
      type: 'boolean',
      width: 150,
      filterable: true,
      filterOperators: booleanFilterOperators,
      sortable: true,
      renderCell: booleanCell(t)
    },
    {
      field: 'warehouse',
      headerName: t('global.warehouse'),
      width: 200,
      filterable: true,
      filterOperators,
      sortable: true,
      renderCell: baseCell
    }
  ];
  if (isDir) {
    columns.push({
      field: 'warehouse_description',
      headerName: t('global.warehouse_description'),
      width: 200,
      type: 'boolean',
      renderCell: baseCell
    });
  }
  columns.push(
    {
      field: 'ref',
      headerName: t(`entities.order_line.ref`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_date',
      headerName: t(`entities.movement.order_date`),
      width: 100,
      filterOperators: dateFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'requested_shipment_date',
      type: 'date',
      headerName: t(`entities.order_line.requested_shipment_date`),
      width: 150,
      renderCell: dateCell
    },

    {
      field: 'remaining_quantity',
      type: 'number',
      headerName: t('entities.order_line.remaining_quantity'),
      width: 150,
      filterOperators: numberFilterOperators,
      sortable: true,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t('global.status'),
      width: 100,
      type: 'singleSelect',
      valueOptions: statusOptions.map((option) => ({
        value: option.value,
        label: option.label
      })),
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) => {
        return translateStatus(params.value);
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <span className={statusColor(params.value)}>{translateStatus(params.value)} </span>
          </div>
        );
      }
    },
    {
      field: 'notes',
      headerName: t('global.notes'),
      width: 300,
      filterOperators,
      renderCell: baseCell
    }
  );

  ['unit_price', 'pending_amount', 'amount'].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`${translationPrefix}.${field}`),
      type: 'number',
      editable: false,
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3),
      hide: isDir
    });
  });

  return columns;
}
